<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import CounterpartyPickerModal from '../components/CounterpartyPickerModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatMoney, toIsoDate } from '../utils/format'
import type {
  FinanceAdjustmentType,
  FinancialAccount,
  Invoice,
  Option,
} from '../types/finance'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const {
  listInvoices,
  listFinancialAccounts,
  createFinanceAdjustment,
  listAllAgentsByRole,
} = useFinanceApi()

const types: FinanceAdjustmentType[] = ['customer_credit', 'write_off', 'debit_note', 'refund']

/** `ADJ-YYYYMMDD-xx` suggestion; the backend only requires uniqueness. */
function suggestNumber(): string {
  const stamp = toIsoDate().replace(/-/g, '')
  const suffix = Math.floor(Math.random() * 90 + 10)
  return `ADJ-${stamp}-${suffix}`
}

const invoices = ref<Invoice[]>([])
const accounts = ref<FinancialAccount[]>([])
const loading = ref(true)
const saving = ref(false)
const loadError = ref('')
const formError = ref('')
const counterpartyPickerOpen = ref(false)
const counterpartyName = ref('')

const form = ref({
  adjustment_number: suggestNumber(),
  adjustment_type: 'customer_credit' as FinanceAdjustmentType,
  invoice_id: '' as string | number,
  amount: 0,
  adjustment_date: toIsoDate(),
  financial_account_id: '' as string | number,
  reason: '',
  notes: '',
  status: 'posted' as 'draft' | 'posted',
})

/** Only non-draft invoices can be adjusted (backend rule). */
const adjustableInvoices = computed(() => invoices.value.filter((invoice) => invoice.status !== 'draft'))

const selectedInvoice = computed(() =>
  adjustableInvoices.value.find((invoice) => String(invoice.id) === String(form.value.invoice_id)),
)

const invoiceCurrency = computed(() => selectedInvoice.value?.currency_code ?? '')

/** Accounts in the invoice currency — a refund needs a matching-currency account. */
const matchingAccounts = computed(() =>
  accounts.value.filter(
    (account) => account.is_active === 1 && account.currency_code === invoiceCurrency.value,
  ),
)

const isRefund = computed(() => form.value.adjustment_type === 'refund')

const validationError = computed(() => {
  if (!form.value.adjustment_number.trim()) {
    return t('finance.common.requiredField', { field: t('finance.adjustments.adjustmentNumber') })
  }
  if (!form.value.invoice_id) {
    return t('finance.common.requiredField', { field: t('finance.common.invoice') })
  }
  if (Number(form.value.amount) <= 0) {
    return t('finance.common.requiredField', { field: t('finance.common.amount') })
  }
  if (!form.value.adjustment_date) {
    return t('finance.common.requiredField', { field: t('finance.common.date') })
  }
  if (!form.value.reason.trim()) {
    return t('finance.common.requiredField', { field: t('finance.common.reason') })
  }
  if (isRefund.value && !form.value.financial_account_id) {
    return t('finance.adjustments.refundAccountRequired')
  }
  return ''
})

/** Counterparty chosen in the picker: load its adjustable invoices. */
async function onCounterpartySelected(counterparty: Option) {
  counterpartyPickerOpen.value = false
  counterpartyName.value = counterparty.name
  form.value.invoice_id = ''
  form.value.amount = 0
  invoices.value = []
  try {
    invoices.value = await listInvoices({ agent_id: counterparty.id })
  } catch (cause) {
    formError.value = formatApiError(cause)
  }
}

/** Picking an invoice sets the currency-dependent defaults. */
function onInvoiceChanged() {
  const invoice = selectedInvoice.value
  form.value.amount = Number(invoice?.total_amount ?? 0)
  const account = matchingAccounts.value.find(
    (entry) => String(entry.id) === String(form.value.financial_account_id),
  )
  if (!account) form.value.financial_account_id = ''
}

async function load() {
  loading.value = true
  loadError.value = ''
  try {
    accounts.value = await listFinancialAccounts({ is_active: 1 })
  } catch (cause) {
    loadError.value = formatApiError(cause)
  } finally {
    loading.value = false
  }

  const agentParam = route.query.agent_id
  if (!agentParam) return
  try {
    const list = await listAllAgentsByRole('customer')
    const match = list.find((agent) => String(agent.id) === String(agentParam))
    if (match) await onCounterpartySelected(match)
    const invoiceParam = route.query.invoice_id
    if (invoiceParam) {
      form.value.invoice_id = String(invoiceParam)
      onInvoiceChanged()
    }
  } catch {
    // The picker stays available if the lookup fails.
  }
}

async function submit() {
  if (validationError.value) {
    formError.value = validationError.value
    return
  }
  saving.value = true
  formError.value = ''
  try {
    await createFinanceAdjustment({
      adjustment_number: form.value.adjustment_number.trim(),
      adjustment_type: form.value.adjustment_type,
      invoice_id: form.value.invoice_id,
      amount: Number(form.value.amount),
      adjustment_date: form.value.adjustment_date,
      status: form.value.status,
      financial_account_id: form.value.financial_account_id || undefined,
      reason: form.value.reason.trim(),
      notes: form.value.notes.trim() || undefined,
    })
    await router.push('/receivables/adjustments')
  } catch (cause) {
    formError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/receivables/adjustments">
      ← {{ t('finance.adjustments.title') }}
    </RouterLink>
    <h2>{{ t('finance.adjustments.newAdjustment') }}</h2>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="loadError" class="state-message error">
    <p>{{ loadError }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <form v-else class="form-card" @submit.prevent="submit">
    <h3>
      {{ t('finance.adjustments.newAdjustment') }}
      <small>{{ t('finance.adjustments.reasonHint') }}</small>
    </h3>

    <div class="form-grid">
      <div class="field">
        <label for="adjustment-agent">{{ t('finance.common.agent') }}</label>
        <button
          id="adjustment-agent"
          class="field-button"
          type="button"
          @click="counterpartyPickerOpen = true"
        >
          <span v-if="counterpartyName">{{ counterpartyName }}</span>
          <span v-else class="placeholder">{{ t('finance.picker.agentTitle') }}</span>
          <span aria-hidden="true">⌕</span>
        </button>
      </div>

      <div class="field">
        <label for="adjustment-invoice">{{ t('finance.common.invoice') }}</label>
        <select id="adjustment-invoice" v-model="form.invoice_id" @change="onInvoiceChanged">
          <option value="">{{ t('finance.adjustments.selectInvoice') }}</option>
          <option v-for="invoice in adjustableInvoices" :key="invoice.id" :value="invoice.id">
            #{{ invoice.id }} — {{ formatMoney(invoice.total_amount, invoice.currency_code) }}
          </option>
        </select>
        <small v-if="counterpartyName && adjustableInvoices.length === 0">
          {{ t('finance.adjustments.noInvoices') }}
        </small>
      </div>

      <div class="field">
        <label for="adjustment-number">{{ t('finance.adjustments.adjustmentNumber') }}</label>
        <input id="adjustment-number" v-model="form.adjustment_number" type="text" autocomplete="off">
        <small>{{ t('finance.adjustments.numberHint') }}</small>
      </div>

      <div class="field">
        <label for="adjustment-type">{{ t('finance.common.type') }}</label>
        <select id="adjustment-type" v-model="form.adjustment_type">
          <option v-for="type in types" :key="type" :value="type">
            {{ t(`finance.adjustmentType.${type}`) }}
          </option>
        </select>
      </div>

      <div class="field">
        <label for="adjustment-amount">{{ t('finance.common.amount') }}</label>
        <input id="adjustment-amount" v-model.number="form.amount" type="number" step="0.01">
        <small v-if="invoiceCurrency">{{ invoiceCurrency }}</small>
      </div>

      <div class="field">
        <label for="adjustment-date">{{ t('finance.common.date') }}</label>
        <input id="adjustment-date" v-model="form.adjustment_date" type="date">
      </div>

      <div class="field">
        <label for="adjustment-account">
          {{ t('finance.common.account') }}
          <template v-if="isRefund">*</template>
        </label>
        <select
          id="adjustment-account"
          v-model="form.financial_account_id"
          :disabled="!invoiceCurrency"
        >
          <option value="">{{ t('finance.common.none') }}</option>
          <option v-for="account in matchingAccounts" :key="account.id" :value="account.id">
            {{ account.name }} — {{ account.currency_code }}
          </option>
        </select>
        <small v-if="isRefund">{{ t('finance.adjustments.refundAccountHint') }}</small>
      </div>

      <div class="field">
        <label for="adjustment-status">{{ t('finance.common.status') }}</label>
        <select id="adjustment-status" v-model="form.status">
          <option value="posted">{{ t('finance.adjustmentStatus.posted') }}</option>
          <option value="draft">{{ t('finance.adjustmentStatus.draft') }}</option>
        </select>
        <small>{{ t('finance.adjustments.statusHint') }}</small>
      </div>

      <div class="field field-wide">
        <label for="adjustment-reason">{{ t('finance.common.reason') }}</label>
        <input id="adjustment-reason" v-model="form.reason" type="text" autocomplete="off">
      </div>

      <div class="field field-wide">
        <label for="adjustment-notes">{{ t('finance.common.notes') }} ({{ t('finance.common.optional') }})</label>
        <textarea id="adjustment-notes" v-model="form.notes" />
      </div>
    </div>

    <p v-if="formError" class="error-inline">{{ formError }}</p>

    <div class="form-actions">
      <button class="btn-primary" type="submit" :disabled="saving">
        {{ saving ? t('finance.common.saving') : t('finance.common.save') }}
      </button>
      <RouterLink class="btn-secondary" to="/receivables/adjustments">
        {{ t('finance.common.cancel') }}
      </RouterLink>
    </div>

    <CounterpartyPickerModal
      :open="counterpartyPickerOpen"
      role="customer"
      @close="counterpartyPickerOpen = false"
      @select="onCounterpartySelected"
    />
  </form>
</template>
